import type { SolanaAddress } from '@funkit/api-base'
import { solanaChain } from '@funkit/chains'
import type { WithdrawalClient } from '@funkit/connect-core'
import type { SolanaWalletSigner } from '@funkit/connect-core/clients/solana'
import type { Address } from 'viem'

import type {
  WithdrawalFlowConfig,
  WithdrawalToken,
} from '../../src/flow/withdrawalFlowConfig'

/** The only Solana source offered today. */
export const SOLANA_USDC_MINT =
  'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' as SolanaAddress

/** USDC on Solana as the rail's source token. */
export const SOLANA_USDC_SOURCE_TOKEN: WithdrawalToken = {
  chainId: String(solanaChain.id),
  // The config's address type is hex until the public widening lands.
  address: SOLANA_USDC_MINT as unknown as Address,
  symbol: 'USDC',
  decimals: 6,
}

/** A Solana withdrawal, as the host configures it. */
export interface SolanaWithdrawalFlowConfig {
  /** The host's connected Solana wallet. The SDK never holds key material. */
  signer: SolanaWalletSigner
}

/** Everything the host sets that is not decided by the Solana source. */
export type SolanaHostWithdrawalConfig = Omit<
  WithdrawalFlowConfig,
  'sourceToken' | 'sourceTokens' | 'withdrawalWallet' | 'swappedAcceptedToken'
>

export function buildSolanaWithdrawalConfig({
  config,
  withdrawalWallet,
}: {
  config: SolanaHostWithdrawalConfig
  withdrawalWallet: WithdrawalClient
}): WithdrawalFlowConfig {
  return {
    ...config,
    // The type omits these, but a reused EVM config still carries them at
    // runtime, and the form would prefer them over the pinned source.
    sourceTokens: undefined,
    swappedAcceptedToken: undefined,
    sourceToken: SOLANA_USDC_SOURCE_TOKEN,
    defaultReceiveToken: config.defaultReceiveToken ?? 'USDC',
    // The shortcut pastes the source address as recipient; a base58 address
    // is the wrong destination for every EVM receive token.
    disableConnectedWallet: config.disableConnectedWallet ?? true,
    withdrawalWallet,
  }
}
